import { css, html, TemplateResult } from 'lit';
import emit from '../../../common/events';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { OffCanvasAction } from '../../off-canvas/interfaces/off-canvas.interface';
import { PLACEMENT_LEFT } from '../../../common/constants/bs';
import { ProductCardData } from '../../product-card/product-card-display';
import { StoreState } from '../../../store/store.interface';
import { WISH_LIST_OFF_CANVAS_IDS } from '../interfaces/wish-list-off-canvas.interface';
import { booleanConverter } from '../../../common/converters';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { getFocusOutOffCanvasAction } from '../../off-canvas/store/off-canvas.selector';
import { getLoginURLWithRedirectUrlToCurrentPage } from '../../../common/utils/login-utils';
import { getWishListsData } from '../store/wish-list.selector';
import { isProductInWishLists } from '../utils/wish-list-product.util';
import { isSuccessUser } from '../../user-profile/user-profile.selectors';
import {
  sendOffCanvasData,
  triggerFocusOutOffCanvasDismiss,
  triggerOpenOffCanvas,
} from '../../off-canvas/store/off-canvas.store';
import { WishListAnalytics } from '../utils/wish-list-analytics.util';

@customElement('mhecomm-wish-list-update-product-button')
export class WishListUpdateProductButton extends connect(store)(BaseComponent) {
  static styles = [
    css`
      .wish-list-icon-tooltip ~ .tooltip > .tooltip-inner {
        min-width: 12.5rem;
      }
    `,
  ];

  /**
   * The placement of the tooltip.
   */
  @property({ type: String })
  tooltipPlacement: string = PLACEMENT_LEFT;

  /**
   * The current product data.
   */
  @property({ type: Object })
  product?: ProductCardData;

  /**
   * Whether the user must be logged in to add the product to the wish list.
   */
  @property({ converter: booleanConverter })
  requireLogin?: boolean = true;

  /**
   * A flag to determine if the user is logged in, if not the button will not be displayed.
   */
  @state()
  private isLoggedInUser = false;

  /**
   * A flag to determine if the product is a part of the wish list.
   */
  @state()
  private isProductInList = false;

  /**
   * The current product ISBN.
   */
  private productIsbn = '';

  /**
   * The analytics instance for the wish list.
   */
  private readonly wishListAnalytics: WishListAnalytics;

  constructor() {
    super();
    this.wishListAnalytics = new WishListAnalytics();
  }

  protected async updated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(_changedProperties);

    this.bsInit('[data-bs-toggle="tooltip"]', window.bootstrap?.Tooltip);
    this.updateProductInListWhenProductChanges(_changedProperties);
  }

  stateChanged(state: StoreState): void {
    this.isLoggedInUser = this.requireLogin ? isSuccessUser(state) : true;
    this.updateProductInList(state);
    const focusOutOffCanvasAction: OffCanvasAction | undefined = getFocusOutOffCanvasAction(state);

    if (focusOutOffCanvasAction && focusOutOffCanvasAction.focusElementSelector) {
      const element = this.shadowRoot?.querySelector(focusOutOffCanvasAction.focusElementSelector) as HTMLElement;
      if (element) {
        element.focus();
        store.dispatch(triggerFocusOutOffCanvasDismiss());
      }
    }
  }

  renderComp(): TemplateResult {
    if (!this.isLoggedInUser) {
      return html` <button
        id="wish-list-product-${this.product?.isbn}-tooltip"
        data-automation-id="wish-list-product-${this.product?.isbn}-tooltip"
        aria-label="Add to Wishlist"
        class="wish-list-icon-tooltip btn border-0 p-0"
        type="button"
        data-bs-toggle="tooltip"
        data-bs-placement=${this.tooltipPlacement}
        title="Login to your PreK-12 account to create and add to wish lists."
        @click="${(event) => {
          this.captureUserLoginAnalytics(event);
          window.location.href = getLoginURLWithRedirectUrlToCurrentPage();
        }}"
      >
        ${this.uxtIconReady ? html` <uxt-icon icon="heart" color="primary" size="lg"></uxt-icon>` : ''}
      </button>`;
    }

    return html`
      <button
        id="wish-list-update-product-${this.product?.isbn}-button"
        data-automation-id="wish-list-update-product-${this.product?.isbn}-button"
        aria-label="Add This Product to a List (opens in a modal)"
        class="btn btn-link text-reset border-0 p-0"
        type="button"
        @click=${() => this.openWishListOffCanvas()}
      >
        ${this.uxtIconReady
          ? html` <uxt-icon icon="${this.isProductInList ? 'heart-fill' : 'heart'}" color="primary" size="lg">
            </uxt-icon>`
          : ''}
      </button>
    `;
  }

  /**
   * Captures the login attemp for the user by click on the heart-icon along with the product details and redirect to heclr login page.
   */
  private captureUserLoginAnalytics(e: Event) {
    this.wishListAnalytics.captureUserLoginAnalytics(this.product!, this);
  }

  /**
   * Dispatches the Redux action to trigger the off-canvas component.
   */
  private openWishListOffCanvas(): void {
    emit({
      element: this,
      name: 'click',
      detail: {
        eventName: 'wish-list-update-product-off-canvas-open',
        isbn: this.product?.isbn13,
        url: window.location.href,
        label: 'Update Product Off-canvas Open',
        comp: 'wish-list',
      },
      dataLayer: false,
    });

    store.dispatch(
      sendOffCanvasData({
        offCanvasId: WISH_LIST_OFF_CANVAS_IDS.PRODUCT_UPDATE,
        data: this.product,
      })
    );
    store.dispatch(
      triggerOpenOffCanvas({
        offCanvasId: WISH_LIST_OFF_CANVAS_IDS.PRODUCT_UPDATE,
        triggerId: `#wish-list-update-product-${this.product?.mhid}-button`,
      })
    );
  }

  /**
   * Updates the flag to determine if the product is in the current wish list.
   */
  private updateProductInList(state: StoreState): void {
    const wishLists = getWishListsData(state);
    if (wishLists) {
      this.isProductInList = isProductInWishLists(this.product, wishLists);
    }
  }

  /**
   * Updates the product in list state when the product changes.
   *
   * This happens for example when navigating to the next page in the pagination.
   *
   * @param _changedProperties
   */
  private updateProductInListWhenProductChanges(_changedProperties: Map<string | number | symbol, unknown>): void {
    if (_changedProperties.has('product') && this.product?.isbn !== this.productIsbn) {
      this.updateProductInList(store.getState() as StoreState);
      this.productIsbn = this.product?.isbn || '';
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-wish-list-update-product-button': WishListUpdateProductButton;
  }
}
